import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { HIDDEN, display, parseLike } from "./cellUtils";
import { t } from "@/lib/i18n";

function Cell({ row, col, onUpdate }) {
  const value = row[col];
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const readOnly = col === "_id" || value === HIDDEN;

  const save = () => {
    setEditing(false);
    if (text === display(value)) return;
    try {
      onUpdate(row._id, { [col]: parseLike(text, value) });
    } catch (e) {
      toast.error(e.message || "Valore non valido");
    }
  };

  if (editing)
    return (
      <input autoFocus value={text} onChange={(e) => setText(e.target.value)} onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        className="w-full min-w-[140px] px-2 py-1 border border-primary rounded text-xs font-mono-j outline-none"
        data-testid={`admin-cell-input-${col}`} />
    );
  return (
    <button type="button" disabled={readOnly} onClick={() => { setText(display(value)); setEditing(true); }}
      title={display(value)} data-testid={`admin-cell-${col}`}
      className={`block w-full max-w-[260px] truncate text-left px-2 py-1 rounded text-xs font-mono-j ${readOnly ? "text-slate-400 cursor-default" : "hover:bg-primary/10 cursor-text"}`}>
      {display(value) || <span className="text-slate-300">—</span>}
    </button>
  );
}

function DeleteButton({ onConfirm }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid="admin-delete-row-btn"><Trash2 className="h-3.5 w-3.5" /></button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("Eliminare questa riga?")}</AlertDialogTitle>
          <AlertDialogDescription>{t("L'operazione è definitiva e non può essere annullata.")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="admin-delete-cancel-btn">{t("Annulla")}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700" data-testid="admin-delete-confirm-btn">{t("Elimina")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function AdminTable({ data, loading, onUpdate, onDelete }) {
  if (loading) return <div className="grid place-items-center h-40"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const cols = data?.columns || [];
  const rows = data?.rows || [];
  if (!rows.length) return <div className="bg-white border border-border rounded-xl p-10 text-center text-muted-foreground" data-testid="admin-empty">{t("Nessuna riga in questa tabella.")}</div>;
  return (
    <div className="bg-white border border-border rounded-xl overflow-auto max-h-[calc(100vh-160px)]" data-testid="admin-table">
      <table className="text-sm min-w-full">
        <thead className="sticky top-0 bg-slate-50 z-10">
          <tr>
            <th className="w-10" />
            {cols.map((c) => <th key={c} className="text-left px-2 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r._id} className="border-t border-border hover:bg-slate-50/60" data-testid={`admin-row-${r._id}`}>
              <td className="px-1"><DeleteButton onConfirm={() => onDelete(r._id)} /></td>
              {cols.map((c) => <td key={c} className="px-1 py-0.5"><Cell row={r} col={c} onUpdate={onUpdate} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
